/**
 * Git Integration HTTP client — base URL and auth headers.
 *
 * Uses a plain axios instance (no 401→logout interceptor) because a 401 from
 * the git-integration service means "git not configured / OIDC exchange failed",
 * not "user session invalid". Auth headers are injected explicitly per-request
 * via gitAuthHeaders(), so no request interceptor is needed either.
 */

import axios from 'axios';
import { getAuthToken } from '@/dataProvider/axios';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import env from '@/config/env';

const cleanUrl = (v: string) => v.replace(/\/+$/, '');

export const GIT_INTEGRATION_BASE_URL = cleanUrl(
  `${cleanUrl(env.API_URL)}/proxy/git-integration`
);

export const gitAxios = axios.create({
  baseURL: GIT_INTEGRATION_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 120000,
  withCredentials: false,
});

// Parse the server's { success: false, error: "[INTERNAL] ..." } body out of
// 4xx/5xx responses so callers see the human-readable message, not the axios
// "Request failed with status code NNN" default.
gitAxios.interceptors.response.use(
  (res) => res,
  (err) => {
    const serverError: string | undefined = err?.response?.data?.error;
    if (serverError) {
      const colonIdx = serverError.indexOf(':');
      err.message = colonIdx !== -1 ? serverError.slice(colonIdx + 1).trim() : serverError;
    }
    return Promise.reject(err);
  },
);

export function gitAuthHeaders(orgId?: string | null): Record<string, string> {
  const state = useAuthStoreV1.getState();
  const headers: Record<string, string> = {};
  const effectiveOrgId = orgId ?? state.selectedOrg?.id;
  if (effectiveOrgId) headers['X-Organization-ID'] = effectiveOrgId;
  if (state.user?.id) headers['X-User-Id'] = state.user.id;
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}
